<template lang="pug">
#send-safety-confirm-to-other-user-confirm
  // title
  .d-flex.flex-row.justify-content-center.mb-2
    .page-title 施設関係者へ安否確認入力依頼のメッセージ送信

  // progress
  ProcessIndicator(v-model="indicator")
  .mb-3.separator

  // warning messages
  template
    .mb-3
      span.error-message {{ errorMessage }}

  // group name
  .mb-0
    span.key-name 宛先（宛先グループ）
  #group-name.mb-3
    .mb-0
      span {{ _groupName }}
  // openingSentence
  .mb-0
    span.key-name メッセージ内容
  #opening-sentence.mb-3
    .mb-0
      span {{ openingSentence }}

  // questions
  .mt-2.mb-0
    span.key-name 質問項目
  #questions.mb-3
    .mb-0
      span {{ questionsLabel }}

  // buttons
  .d-flex.flex-row.justify-content-center
    b-button.btn.send-btn.mr-2(@click="onSend") 送信
    b-button.btn.back-btn(@click="onCancel") 戻る

  // loading component
  Loading(:isBusy="isBusy", :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";
import { Getter } from "vuex-class";
import { userStore, mobileSendSafetyConfirmToOtherUserStore } from "@/store/store";
import { MobileSendSafetyConfirmToOtherUserState } from "@/store/MobileSendSafetyConfirmToOtherUser";
import { MobileUrl } from "@/commons/constants/MobileUrl";
import SystemError from "@/commons/models/SystemError";

import SendSafetyConfirmMessagesService, {
  OtherUserSendSafetyConfirmLineMessagesPostModel,
  OtherUserSendSafetyConfirmMailMessagesPostModel,
  SafetyConfirmTarget
} from "@/commons/services/SendSafetyConfirmMessages";
import SendMessageGroupsService from "@/commons/services/SendMessageGroups";
import SendMessageGroupMembersService, { SendMessageGroupMembersOtherUserGroupMemberGetModel } from "@/commons/services/SendMessageGroupMembers";
import OtherUserGroup, { SenderType } from "@/commons/models/OtherUserGroup";
import OtherUserGroupMembersService from "@/commons/services/OtherUserGroupMembers";

import Loading from "@/mobile/basics/Loading.vue";
import ProcessIndicator, { ProcessIndicatorModel } from "@/mobile/basics/ProcessIndicator.vue";
import { UseId } from "@/mobile/components/SafetyConfirmOtherUserQuestions.vue";
import { SafetyConfirmTargetMessageGroupModalModel, TargetType } from "@/mobile/components/modals/SafetyConfirmTargetMessageGroupModal.vue";

/**
 * ### 安否確認入力依頼のメッセージ送信(施設関係者) 確認コンポーネント
 */
@Component({
  components: {
    Loading,
    ProcessIndicator
  }
})
export default class SendSafetyConfirmToOtherUserConfirm extends Vue {
  /** 安否確認(施設関係者)メッセージ送信サービス */
  @Inject("SendSafetyConfirmMessagesService") sendSafetyConfirmMessagesService!: SendSafetyConfirmMessagesService;
  /** 宛先グループサービス */
  @Inject("SendMessageGroupsService") sendMessageGroupsService!: SendMessageGroupsService;
  /** 宛先グループメンバーサービス */
  @Inject("SendMessageGroupMembersService") sendMessageGroupMembersService!: SendMessageGroupMembersService;
  /** 施設関係者グループメンバー取得サービス */
  @Inject("OtherUserGroupMembersService") otherUserGroupMembersService!: OtherUserGroupMembersService;

  /** ### 安否確認事前設定(施設関係者)画面での入力情報 */
  @Getter("mobileSendSafetyConfirmToOtherUserState") state!: MobileSendSafetyConfirmToOtherUserState;

  /** インジケータ */
  indicator: Array<ProcessIndicatorModel> = [
    { text: "入力", current: false },
    { text: "確認", current: true },
    { text: "完了", current: false }
  ];

  /** 読み込み中モーダル表示可否 */
  isBusy: boolean = false;
  /** 読み込み中モーダルメッセージ */
  loadingMessage: string = "送信中";

  /** 文書送付ID */
  safetyConfirmId: number | null = null;
  /** グループ */
  groupTargets: Array<SafetyConfirmTargetMessageGroupModalModel> = [];
  /** 冒頭文 */
  openingSentence: string = "";
  /** 質問用ラベル */
  questionsLabel: string = "";
  /** 質問IDリスト */
  useIds: number[] = [];

  /** エラーメッセージ */
  errorMessage: string = "";

  get _groupName(): string {
    if (!this.groupTargets || this.groupTargets.length < 1) {
      return "";
    }

    const names: string[] = [];
    for (const groupTarget of this.groupTargets) {
      for (const target of groupTarget.targets) {
        if (target.isTarget) {
          names.push(target.name);
        }
      }
    }
    return names.join("、");
  }

  /**
   * ### vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    // ブラウザバック対応。メッセージ送信後にブラウザバックされた場合にstoreに内容がないため表示する内容が無い
    // storeがリセットされていたら入力画面へ遷移させる
    if (this.state.exist == false) {
      this.$router.push(MobileUrl.SendSafetyConfirmToOtherUserInput);
    }

    await this.createView();
    return;
  }

  /**
   * ### 画面表示情報を作成する
   */
  public async createView(): Promise<void> {
    this.isBusy = true;

    this.safetyConfirmId = this.state.safetyConfirmId;
    this.groupTargets = this.state.groupTargets;
    this.openingSentence = this.state.openingSentence;
    this.questionsLabel = this.state.questionsLabel;
    this.useIds = this.state.useIds;

    this.isBusy = false;

    return;
  }

  /**
   * ### 送信ボタン押下イベント
   */
  public async onSend(): Promise<void> {
    this.isBusy = true;

    try {
      const otherUserGroupMembersInFacility: Array<OtherUserGroup> = await this.otherUserGroupMembersService.get(userStore);
      const otherUserGroupMemberMap: Map<number, Array<OtherUserGroup>> = this.createOtherUserGroupMemberMap(otherUserGroupMembersInFacility);

      const requestBodyForLine: OtherUserSendSafetyConfirmLineMessagesPostModel = await this.createRequestBodyToLine(this.groupTargets, otherUserGroupMemberMap);
      const requestBodyForMail: OtherUserSendSafetyConfirmMailMessagesPostModel = await this.createRequestBodyToMail(this.groupTargets, otherUserGroupMemberMap);

      if (requestBodyForLine.targets.length < 1 && requestBodyForMail.targets.length < 1) {
        this.errorMessage = "送信対象の施設関係者がいないため、メッセージを送ることができません";
      } else {
        if (0 < requestBodyForLine.targets.length) {
          await this.sendSafetyConfirmMessagesService.sendOtherUserMessage(userStore, this.safetyConfirmId as number, requestBodyForLine);
        }
        if (0 < requestBodyForMail.targets.length) {
          await this.sendSafetyConfirmMessagesService.sendOtherUserMails(userStore, this.safetyConfirmId as number, requestBodyForMail);
        }

        mobileSendSafetyConfirmToOtherUserStore.resetMobileSendSafetyConfirmToOtherUserState();
        this.$router.push(MobileUrl.SendSafetyConfirmToOtherUserComplete);
      }
    } catch (error) {
      const systemError: SystemError = error as SystemError;

      // ステータスコードが400の場合、エラーメッセージを表示し画面情報を再取得する
      if (systemError.statusCode == 400 && 0 < systemError.originalResponse.indexOf("other user safety confirmation has already been sent.")) {
        this.createView();
        this.errorMessage = "既に施設関係者（職員）の安否確認を送信しているため送信できません。";
      } else {
        throw error;
      }
    } finally {
      this.isBusy = false;
    }

    return;
  }

  private createOtherUserGroupMemberMap(otherUserGroupMembersInFacility: Array<OtherUserGroup>): Map<number, Array<OtherUserGroup>> {
    const map: Map<number, Array<OtherUserGroup>> = new Map<number, Array<OtherUserGroup>>();
    for (const member of otherUserGroupMembersInFacility) {
      const members: Array<OtherUserGroup> | undefined = map.get(member.id);
      if (members) {
        members.push(member);
        map.set(member.id, members);
      } else {
        map.set(member.id, [member]);
      }
    }
    return map;
  }

  /**
   * ### 安否確認登録リクエストボディ作成
   */
  public async createRequestBodyToLine(
    groupTargets: Array<SafetyConfirmTargetMessageGroupModalModel>,
    otherUserGroupMemberMap: Map<number, Array<OtherUserGroup>>
  ): Promise<OtherUserSendSafetyConfirmLineMessagesPostModel> {
    const targets: Array<SafetyConfirmTarget> = [];

    // 宛先グループ（施設関係者）のメンバーのLINEユーザーで宛先を作成する
    for (const groupType of groupTargets) {
      switch (groupType.type) {
        case TargetType.SEND_MESSAGE_GROUP_FOR_OTHER_USER: {
          for (const target of groupType.targets) {
            if (target.isTarget) {
              const sortKeys: Array<string> = [];
              const members: Array<SendMessageGroupMembersOtherUserGroupMemberGetModel> = await this.getSendMessageGroupOtherGroupUserMembers(target.id as number);

              for (const member of members) {
                if (member.senderType == SenderType.LINE && member.memberLineId) {
                  sortKeys.push(member.otherUserGroupsSortKey);
                }
              }

              if (0 < sortKeys.length) {
                targets.push({
                  groupName: target.name,
                  sortKeys
                });
              }
            }
          }
          break;
        }
        // 施設関係者グループのメンバーのLINEユーザーで宛先を作成する
        case TargetType.OTHER_USER_GROUP: {
          for (const target of groupType.targets) {
            if (target.isTarget) {
              if (otherUserGroupMemberMap.has(target.id)) {
                const sortKeys: Array<string> = [];

                for (const member of otherUserGroupMemberMap.get(target.id) as Array<OtherUserGroup>) {
                  if (member.senderType == SenderType.LINE && member.memberLineId) {
                    sortKeys.push(member.otherUserGroupsSortKey);
                  }
                }

                if (0 < sortKeys.length) {
                  targets.push({
                    groupName: target.name,
                    sortKeys
                  });
                }
              }
            }
          }
        }
      }
    }

    return {
      targets,
      message: this.openingSentence,
      useIds: this.useIds
    };
  }

  /**
   * ### 安否確認登録リクエストボディ(メール)作成
   */
  public async createRequestBodyToMail(
    groupTargets: Array<SafetyConfirmTargetMessageGroupModalModel>,
    otherUserGroupMemberMap: Map<number, Array<OtherUserGroup>>
  ): Promise<OtherUserSendSafetyConfirmMailMessagesPostModel> {
    const targets: Array<SafetyConfirmTarget> = [];

    // 宛先グループ（施設関係者）のメンバーのLINEユーザーで宛先を作成する
    for (const groupType of groupTargets) {
      switch (groupType.type) {
        case TargetType.SEND_MESSAGE_GROUP_FOR_OTHER_USER: {
          for (const target of groupType.targets) {
            if (target.isTarget) {
              const sortKeys: Array<string> = [];
              const members: Array<SendMessageGroupMembersOtherUserGroupMemberGetModel> = await this.getSendMessageGroupOtherGroupUserMembers(target.id as number);

              for (const member of members) {
                if (member.senderType == SenderType.MAIL && member.memberMailaddress) {
                  sortKeys.push(member.otherUserGroupsSortKey);
                }
              }

              if (0 < sortKeys.length) {
                targets.push({
                  groupName: target.name,
                  sortKeys
                });
              }
            }
          }
          break;
        }
        // 施設関係者グループのメンバーのLINEユーザーで宛先を作成する
        case TargetType.OTHER_USER_GROUP: {
          for (const target of groupType.targets) {
            if (target.isTarget) {
              if (otherUserGroupMemberMap.has(target.id)) {
                const sortKeys: Array<string> = [];

                for (const member of otherUserGroupMemberMap.get(target.id) as Array<OtherUserGroup>) {
                  if (member.senderType == SenderType.MAIL && member.memberMailaddress) {
                    sortKeys.push(member.otherUserGroupsSortKey);
                  }
                }

                if (0 < sortKeys.length) {
                  targets.push({
                    groupName: target.name,
                    sortKeys
                  });
                }
              }
            }
          }
        }
      }
    }

    return {
      targets: targets,
      message: this.openingSentence
    };
  }

  /**
   * ### 宛先グループメンバーを取得する
   */
  public async getSendMessageGroupOtherGroupUserMembers(groupId: number): Promise<Array<SendMessageGroupMembersOtherUserGroupMemberGetModel>> {
    const members: Array<SendMessageGroupMembersOtherUserGroupMemberGetModel> = (await this.sendMessageGroupMembersService.get(
      userStore,
      groupId
    )) as Array<SendMessageGroupMembersOtherUserGroupMemberGetModel>;

    return members;
  }

  /**
   * ### 戻るボタン押下イベント
   *
   * - 安否確認 登録情報 入力画面へ遷移する
   */
  public onCancel(): void {
    this.$router.push({ name: MobileUrl.SendSafetyConfirmToOtherUserInput, params: { id: String(this.safetyConfirmId) } });
    return;
  }

  /**
   * ### 質問事項の項目名を結合した表示用ラベルを作成する
   */
  public createQuestionsLabel(confirms: Array<UseId>): string {
    let labels: string[] = [];

    for (const confirm of confirms) {
      labels.push(confirm.label);
    }

    return labels.join("、");
  }
}
</script>

<style lang="scss" scopeed>
#send-safety-confirm-to-other-user-confirm {
  #group-name,
  #opening-sentence,
  #questions {
    white-space: pre-wrap;
    word-wrap: break-word;
  }
  .send-btn,
  .back-btn {
    min-width: 84px;
  }
}
</style>
